import {ref, computed} from 'vue'
import {authStore} from '@/composables/store-accessor'
import {useNuxtApp} from '#app'

// Define interfaces (can be moved to a shared types file)
export interface Supplier {
    id: string
    name: string
    emails?: string[]
    error?: string
    warning?: string
}

export interface SyncResults {
    success: Supplier[]
    failed: Supplier[]
}

export interface SupplierSyncState {
    id: string
    name: string
    intercomStatus: 'synced' | 'pending' | 'error'
    usersTotal: number
    usersAssociated: number
    usersMissingEmail: number
    usersWithoutEmail?: string[]
    error?: string
}

export function useMessagingSystem() {
    const nuxtApp = useNuxtApp()
    const {$api} = nuxtApp // Assuming $api is available like this

    // ZSH company properties
    const error = ref<string | null>(null)
    const errorDetails = ref<any>(null)
    const isLoading = ref(false)
    const isZshCompanyCreated = ref<boolean | null>(null)
    const zshCompanyInternalId = ref<string | null>(null)

    // Supplier companies properties
    const suppliers = ref<Supplier[]>([])
    const intercomCompanies = ref<string[]>([])
    const suppliersWithoutIntercom = ref<Supplier[]>([])
    const suppliersWithIntercom = ref(0)
    const isSuppliersLoading = ref(false)
    const supplierError = ref<string | null>(null)
    const supplierErrorDetails = ref<any>(null)

    // Sync properties
    const isSyncingSuppliers = ref(false)
    const syncProgress = ref(0)
    const currentSyncItem = ref(0)
    const totalSyncItems = ref(0)
    const syncResults = ref<SyncResults>({success: [], failed: []})

    // User association properties
    const isAssociatingUsers = ref(false)
    const associatedUsers = ref(0)
    const lastSyncOperation = ref<'companies' | 'users' | 'complete' | null>(null)

    // Per-supplier sync state
    const supplierSyncStates = ref<SupplierSyncState[]>([])

    const isTO1User = computed(() => authStore.isTO1)

    // Computed summary stats
    const syncedCount = computed(() => supplierSyncStates.value.filter(s => s.intercomStatus === 'synced').length)
    const pendingCount = computed(() => supplierSyncStates.value.filter(s => s.intercomStatus === 'pending').length)
    const errorCount = computed(() => supplierSyncStates.value.filter(s => s.intercomStatus === 'error').length)
    const incompleteCount = computed(() => supplierSyncStates.value.filter(
        s => s.intercomStatus === 'synced' && s.usersTotal > 0 && s.usersAssociated < s.usersTotal
    ).length)

    async function checkCompanyExists() {
        error.value = null
        errorDetails.value = null
        isLoading.value = true
        isZshCompanyCreated.value = null

        try {
            const token = useCookie('token').value
            if (!token) throw new Error('Authentication token not found. Please log in.')

            const response = await fetch('/proxy/intercom/companies?company_id=zero_star_hotels', {
                headers: {Accept: 'application/json', Token: token},
            })

            if (response.status === 404) {
                isZshCompanyCreated.value = false
                return
            }
            if (!response.ok) {
                let errorMessage = `Error: ${response.status}`
                let errDetails = null
                try {
                    const errorData = await response.json()
                    errorMessage = errorData.message || errorMessage
                    errDetails = errorData
                } catch (e) {
                    /* ignore */
                }
                const fetchError = new Error(errorMessage) as Error & {details?: any}
                fetchError.details = errDetails
                throw fetchError
            }
            isZshCompanyCreated.value = true

            // Capture the internal Intercom ID from the response
            try {
                const companyData = await response.json()
                if (companyData?.id) {
                    zshCompanyInternalId.value = companyData.id
                }
            } catch { /* ignore */ }
        } catch (err: any) {
            error.value = err.message || 'Error checking company'
            errorDetails.value = err.details || null
            console.error('Error checking company (Composable):', err)
        } finally {
            isLoading.value = false
        }
    }

    async function createCompany() {
        error.value = null
        errorDetails.value = null
        isLoading.value = true

        try {
            const token = useCookie('token').value
            if (!token) throw new Error('Authentication token not found. Please log in.')

            const currentTimestamp = Math.floor(Date.now() / 1000)
            const response = await fetch('/proxy/intercom/companies', {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    Token: token,
                },
                body: JSON.stringify({
                    name: 'Zero Star Hotels',
                    company_id: 'zero_star_hotels',
                    remote_created_at: currentTimestamp,
                    currentUserEmail: authStore.person?.email,
                    currentUserName: `${authStore.person?.firstName || ''} ${authStore.person?.lastName || ''}`.trim(),
                }),
            })

            if (!response.ok) {
                let errorMessage = `Error: ${response.status}`
                let errDetails = null
                try {
                    const errorData = await response.json()
                    errorMessage = errorData.message || errorMessage
                    errDetails = errorData
                } catch (e) {
                    /* ignore */
                }
                const fetchError = new Error(errorMessage) as Error & {details?: any}
                fetchError.details = errDetails
                throw fetchError
            }
            isZshCompanyCreated.value = true

            // Capture the internal Intercom ID from the create response
            try {
                const createdData = await response.json()
                if (createdData?.id) {
                    zshCompanyInternalId.value = createdData.id
                }
            } catch { /* ignore */ }

            if (isTO1User.value) {
                try {
                    await ensureTO1UserAssociated()
                } catch (associationErr) {
                    console.error('Error associating TO1 user with new company (Composable):', associationErr)
                }
            }
        } catch (err: any) {
            error.value = err.message || 'Error creating company'
            errorDetails.value = err.details || null
            console.error('Error creating company (Composable):', err)
        } finally {
            isLoading.value = false
        }
    }

    async function loadSuppliers() {
        try {
            const response = await $api.companies.get({
                relationship: 'SUPPLIER',
                type: ['HUB_SUPPLIER', 'INTERNAL_SUPPLIER'],
                shortResponse: true,
                order: 'ALPHABET',
            })
            suppliers.value = response.companies.map((company: any) => ({
                id: company.id,
                name: company.name[company.language] || company.name,
                emails: company.emails || [],
            }))
        } catch (err) {
            console.error('Error loading suppliers (Composable):', err)
            throw new Error('Failed to load suppliers')
        }
    }

    async function loadIntercomCompanies() {
        try {
            const token = useCookie('token').value
            if (!token) throw new Error('Authentication token not found.')

            const response = await fetch('/proxy/intercom/companies/list', {
                headers: {Accept: 'application/json', Token: token},
            })
            if (!response.ok) {
                throw new Error(`Failed to load Intercom companies: ${response.status}`)
            }
            const data = await response.json()
            intercomCompanies.value = data.companies
                .filter((company: any) => company.company_id)
                .map((company: any) => company.company_id.toString())
        } catch (err) {
            console.error('Error loading Intercom companies (Composable):', err)
            throw new Error('Failed to load Intercom companies')
        }
    }

    function checkSuppliersWithIntercom() {
        suppliersWithoutIntercom.value = []
        for (const supplier of suppliers.value) {
            if (!intercomCompanies.value.includes(supplier.id.toString())) {
                suppliersWithoutIntercom.value.push(supplier)
            }
        }
        suppliersWithIntercom.value = suppliers.value.length - suppliersWithoutIntercom.value.length
    }

    async function loadCachedSyncStates(): Promise<Record<string, Partial<SupplierSyncState>>> {
        // Try localStorage first (fast cache)
        try {
            const cached = localStorage.getItem('supplierSyncStates')
            if (cached) {
                const parsed = JSON.parse(cached) as SupplierSyncState[]
                const map: Record<string, Partial<SupplierSyncState>> = {}
                for (const s of parsed) {
                    map[s.id] = s
                }
                return map
            }
        } catch { /* ignore */ }

        // Fallback to MongoDB
        try {
            const token = useCookie('token').value
            if (token) {
                const response = await fetch('/proxy/messaging/suppliers', {
                    headers: {Accept: 'application/json', Token: token},
                })
                if (response.ok) {
                    const data = await response.json()
                    if (data.suppliers && Array.isArray(data.suppliers)) {
                        const map: Record<string, Partial<SupplierSyncState>> = {}
                        for (const s of data.suppliers) {
                            const id = s.supplierId || s.id
                            map[id] = {...s, id}
                        }
                        // Populate localStorage cache
                        try {
                            const asList = Object.values(map)
                            localStorage.setItem('supplierSyncStates', JSON.stringify(asList))
                        } catch { /* ignore */ }
                        return map
                    }
                }
            }
        } catch (err) {
            console.error('Error loading supplier sync states from MongoDB:', err)
        }

        return {}
    }

    async function saveCachedSyncStates() {
        // Write to localStorage (fast cache)
        try {
            localStorage.setItem('supplierSyncStates', JSON.stringify(supplierSyncStates.value))
        } catch { /* ignore */ }

        // Write to MongoDB (source of truth)
        try {
            const token = useCookie('token').value
            if (token) {
                const suppliers = supplierSyncStates.value.map(s => ({
                    supplierId: s.id,
                    name: s.name,
                    intercomStatus: s.intercomStatus,
                    usersTotal: s.usersTotal,
                    usersAssociated: s.usersAssociated,
                    usersMissingEmail: s.usersMissingEmail,
                    lastSyncedAt: s.intercomStatus === 'synced' ? new Date().toISOString() : null,
                    error: s.error || null,
                }))
                await fetch('/proxy/messaging/suppliers', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json', Token: token},
                    body: JSON.stringify({suppliers}),
                })
            }
        } catch (err) {
            console.error('Error saving supplier sync states to MongoDB:', err)
        }
    }

    async function populateSupplierSyncStates() {
        const intercomSet = new Set(intercomCompanies.value)
        const cached = await loadCachedSyncStates()

        supplierSyncStates.value = suppliers.value.map(supplier => {
            const isSyncedInIntercom = intercomSet.has(supplier.id.toString())
            const cachedState = cached[supplier.id]

            // If Intercom confirms synced, use that. Otherwise check local cache.
            const isSynced = isSyncedInIntercom || cachedState?.intercomStatus === 'synced'

            return {
                id: supplier.id,
                name: supplier.name,
                intercomStatus: isSynced ? 'synced' : (cachedState?.intercomStatus === 'error' ? 'error' : 'pending'),
                usersTotal: cachedState?.usersTotal || supplier.emails?.length || 0,
                usersAssociated: cachedState?.usersAssociated || 0,
                usersMissingEmail: cachedState?.usersMissingEmail || 0,
                error: isSynced ? undefined : cachedState?.error,
            } as SupplierSyncState
        })
    }

    async function loadSuppliersAndCheckIntercom() {
        isSuppliersLoading.value = true
        supplierError.value = null
        supplierErrorDetails.value = null
        try {
            await loadSuppliers()
            await loadIntercomCompanies()
            checkSuppliersWithIntercom()
            await populateSupplierSyncStates()
        } catch (err: any) {
            supplierError.value = err.message || 'Error loading supplier data'
            supplierErrorDetails.value = err.details || null
            console.error('Error loading suppliers/Intercom data (Composable):', err)
        } finally {
            isSuppliersLoading.value = false
        }
    }

    async function syncAll() {
        const toSync = supplierSyncStates.value.filter(s =>
            s.intercomStatus === 'pending' ||
            s.intercomStatus === 'error' ||
            (s.intercomStatus === 'synced' && s.usersTotal > 0 && s.usersAssociated < s.usersTotal)
        )
        if (toSync.length === 0) return

        isSyncingSuppliers.value = true
        syncProgress.value = 0
        currentSyncItem.value = 0
        totalSyncItems.value = toSync.length
        syncResults.value = {success: [], failed: []}
        lastSyncOperation.value = 'complete'

        try {
            const token = useCookie('token').value
            if (!token) throw new Error('Authentication token not found.')

            for (let i = 0; i < toSync.length; i++) {
                const supplierState = toSync[i]
                currentSyncItem.value = i + 1
                syncProgress.value = (currentSyncItem.value / totalSyncItems.value) * 100

                // Find the full supplier data
                const supplier = suppliers.value.find(s => s.id === supplierState.id)
                if (!supplier) continue

                try {
                    const response = await fetch('/proxy/intercom/associate-users', {
                        method: 'POST',
                        headers: {
                            Accept: 'application/json',
                            'Content-Type': 'application/json',
                            Token: token,
                        },
                        body: JSON.stringify({
                            company_id: supplier.id.toString(),
                            company_name: supplier.name,
                            emails: supplier.emails || [],
                        }),
                    })

                    if (!response.ok) {
                        let errorMessage = `HTTP ${response.status}`
                        try {
                            const errorData = await response.json()
                            errorMessage = errorData.message || errorMessage
                        } catch (e) {
                            /* ignore */
                        }
                        throw new Error(errorMessage)
                    }

                    const data = await response.json()

                    // Update supplier sync state from response
                    const newlyAssociated = data.newlyAssociated || 0
                    const alreadyAssociated = data.alreadyAssociated || 0
                    const failed = data.failed || 0
                    const skipped = data.skipped || 0
                    const totalUsers = data.totalUsers || (supplier.emails?.length || 0)

                    supplierState.intercomStatus = 'synced'
                    supplierState.usersTotal = totalUsers
                    supplierState.usersAssociated = newlyAssociated + alreadyAssociated
                    supplierState.usersMissingEmail = data.missingEmail || 0
                    supplierState.usersWithoutEmail = data.usersWithoutEmail || []
                    supplierState.error = undefined

                    syncResults.value.success.push(supplier)
                } catch (err: any) {
                    const errorMessage = err.message || 'Unknown error'
                    console.error(`Error syncing supplier ${supplier.name}:`, errorMessage)

                    supplierState.intercomStatus = 'error'
                    supplierState.error = errorMessage

                    syncResults.value.failed.push({...supplier, error: errorMessage})
                }
            }

            // Update summary counts from local state (don't re-query Intercom due to eventual consistency)
            suppliersWithIntercom.value = syncedCount.value
            suppliersWithoutIntercom.value = suppliers.value.filter(
                s => !supplierSyncStates.value.find(ss => ss.id === s.id && ss.intercomStatus === 'synced')
            )

            if (syncResults.value.success.length > 0) {
                nuxtApp.$toast.success(`Sync completed: ${syncResults.value.success.length} suppliers synced`)
            }
            if (syncResults.value.failed.length > 0) {
                nuxtApp.$toast.error(`${syncResults.value.failed.length} suppliers failed to sync`)
            }
        } catch (err: any) {
            supplierError.value = err.message || 'Error during sync'
            console.error('Error during syncAll (Composable):', err)
            nuxtApp.$toast.error(supplierError.value)
        } finally {
            isSyncingSuppliers.value = false
            syncProgress.value = 100
            await saveCachedSyncStates()
        }
    }

    async function ensureTO1UserAssociated() {
        if (!isTO1User.value) return
        try {
            const token = useCookie('token').value
            if (!token) throw new Error('Auth token not found.')

            const currentUserEmail = authStore.person?.email
            const currentUserId = authStore.person?.id
            if (!currentUserEmail || !currentUserId) {
                console.warn('Current user email or ID not found (Composable)')
                return
            }

            if (isZshCompanyCreated.value !== true) {
                return
            }

            // Get company internal ID
            const companyResponse = await fetch('/proxy/intercom/companies?company_id=zero_star_hotels', {
                headers: {Accept: 'application/json', Token: token},
            })
            if (!companyResponse.ok) throw new Error('Failed to get ZSH company info for association.')
            const companyData = await companyResponse.json()
            const companyInternalId = companyData.id
            if (!companyInternalId) throw new Error('Could not get ZSH company internal ID.')

            // Search for contact
            const searchResponse = await fetch('/proxy/intercom/contacts/search', {
                method: 'POST',
                headers: {Accept: 'application/json', 'Content-Type': 'application/json', Token: token},
                body: JSON.stringify({query: {field: 'email', operator: '=', value: currentUserEmail}}),
            })
            if (!searchResponse.ok) throw new Error('Failed to search for TO1 contact.')
            const searchData = await searchResponse.json()
            let contactId = null
            let isAlreadyAssociated = false

            if (searchData.data && searchData.data.length > 0) {
                const existingContact = searchData.data[0]
                contactId = existingContact.id
                const contactDetailsResp = await fetch(`/proxy/intercom/contacts/${contactId}`, {
                    headers: {Accept: 'application/json', Token: token},
                })
                if (contactDetailsResp.ok) {
                    const contactDetails = await contactDetailsResp.json()
                    if (contactDetails.external_id !== currentUserId.toString()) {
                        await fetch(`/proxy/intercom/contacts/${contactId}`, {
                            method: 'PUT',
                            headers: {Accept: 'application/json', 'Content-Type': 'application/json', Token: token},
                            body: JSON.stringify({external_id: currentUserId.toString()}),
                        })
                    }
                    if (contactDetails.companies && contactDetails.companies.data) {
                        isAlreadyAssociated = contactDetails.companies.data.some((c: any) => c.id === companyInternalId)
                    }
                }
            }

            if (!contactId) {
                const createResponse = await fetch('/proxy/intercom/contacts', {
                    method: 'POST',
                    headers: {Accept: 'application/json', 'Content-Type': 'application/json', Token: token},
                    body: JSON.stringify({
                        role: 'user',
                        email: currentUserEmail,
                        name:
                            `${authStore.person?.firstName || ''} ${authStore.person?.lastName || ''}`.trim() ||
                            currentUserEmail.split('@')[0],
                        external_id: currentUserId.toString(),
                    }),
                })
                if (!createResponse.ok) throw new Error('Failed to create TO1 contact.')
                const createData = await createResponse.json()
                contactId = createData.id
            }

            if (!isAlreadyAssociated && contactId) {
                const associateResponse = await fetch(`/proxy/intercom/companies/${companyInternalId}/contacts`, {
                    method: 'POST',
                    headers: {Accept: 'application/json', 'Content-Type': 'application/json', Token: token},
                    body: JSON.stringify({id: contactId}),
                })
                if (!associateResponse.ok) throw new Error('Failed to associate TO1 contact with ZSH company.')
            }
        } catch (err) {
            console.error('Error ensuring TO1 user association (Composable):', err)
        }
    }

    async function loadAndCacheCompanyId() {
        try {
            const token = useCookie('token').value
            if (!token) throw new Error('Auth token not found.')

            const response = await fetch('/proxy/intercom/companies/filtered-list', {
                headers: {Accept: 'application/json', Token: token},
            })

            if (!response.ok) {
                let errorMessage = `HTTP ${response.status}`
                try {
                    const errorData = await response.json()
                    errorMessage = errorData.message || errorMessage
                    console.error('Proxy filtered companies list API error details:', errorData)
                } catch (e) {
                    console.error('Failed to parse error response')
                }
                throw new Error(`Failed to load companies list: ${errorMessage}`)
            }

            const data = await response.json()
            if (data && data.success && data.zeroStarHotelsCompanyId) {
                // Write to localStorage (fast cache)
                try {
                    localStorage.setItem('zeroStarHotelsCompanyId', data.zeroStarHotelsCompanyId)
                } catch { /* ignore */ }
                // Write to MongoDB (source of truth)
                try {
                    await fetch('/proxy/messaging/config', {
                        method: 'POST',
                        headers: {'Content-Type': 'application/json', Token: token},
                        body: JSON.stringify({key: 'zeroStarHotelsCompanyId', value: data.zeroStarHotelsCompanyId}),
                    })
                } catch (mongoErr) {
                    console.error('Error saving zeroStarHotelsCompanyId to MongoDB:', mongoErr)
                }
                return data.zeroStarHotelsCompanyId
            } else {
                throw new Error('Failed to find zero_star_hotels company ID in API response')
            }
        } catch (err: any) {
            console.error('Error loading/caching ZSH company ID (Composable):', err)

            if (err.message.includes('404')) {
                console.warn('Companies list endpoint not found. This might be a configuration issue.')
            } else if (err.message.includes('401')) {
                console.warn('Authentication failed for companies list. Check token validity.')
            } else if (err.message.includes('500')) {
                console.warn('Server configuration issue. Check Intercom API key configuration.')
            }

            return null
        }
    }

    return {
        error,
        errorDetails,
        isLoading,
        isZshCompanyCreated,
        zshCompanyInternalId,
        suppliers,
        intercomCompanies,
        suppliersWithoutIntercom,
        suppliersWithIntercom,
        isSuppliersLoading,
        supplierError,
        supplierErrorDetails,
        isSyncingSuppliers,
        syncProgress,
        currentSyncItem,
        totalSyncItems,
        syncResults,
        isAssociatingUsers,
        associatedUsers,
        lastSyncOperation,
        isTO1User,
        // New per-supplier sync state
        supplierSyncStates,
        syncedCount,
        pendingCount,
        errorCount,
        incompleteCount,
        // Functions
        checkCompanyExists,
        createCompany,
        loadSuppliersAndCheckIntercom,
        syncAll,
        ensureTO1UserAssociated,
        loadAndCacheCompanyId,
        // Expose individual loaders if needed
        loadSuppliers,
        loadIntercomCompanies,
        checkSuppliersWithIntercom,
    }
}
